import { Grid, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { flatMap, isEmpty, map } from 'es-toolkit/compat';
import React, { useRef } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import { getUploadList } from 'api/uploads';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import Loader, { LoadingStage } from 'components/Loader';
import { getDefaultManifestDialogUrlState } from 'components/StudyDashboard/CaseManifest/dialogState';
import { getOrganizationEntryUrl } from 'components/StudyDashboard/CaseManifest/organizationEntry';
import { getActionableUploadGroups } from 'components/StudyDashboard/StudyUploadsStatus';
import { WorkflowTab, useStudyDashboardTabState } from 'components/StudyDashboard/useStudyDashboardTabState';
import { getDashboardCanvasStyles } from 'components/theme/theme';
import { JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { ReviewForm } from 'interfaces/reviewForm';
import { Study } from 'interfaces/study';
import { isPfizerDemoLab } from 'utils/demo';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentStateCases } from 'utils/useCurrentStateCases';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { getUrlToJobsPage } from 'utils/useNavigationToJobsPage';
import { DataGridInterfaceWrapper } from './DataGrids/DataGridInterfaceWrapper';
import { ErrorMessage } from './ErrorMessage';
import { GalleryViewer } from './GalleryViewer';
import { UnpaginatedGalleryViewer } from './GalleryViewer/UnpaginatedGalleryViewer';
import ProcedureCards from './ProcedureCards/ProcedureCards';
import StudySlidesEmptyState from './StudySlidesEmptyState';
import { useCasesAndSlidesEditingPermissions } from './useCasesAndSlidesEditingPermissions';
import { useCasesViewMode } from './useCasesViewMode';
import { useAllPendingSlides, useStudyPendingSlidesCount } from './usePendingSlides';

export const CasesDisplay: React.FC<
  React.PropsWithChildren<{
    displayProcedureModeToggle?: boolean;
    pendingSlidesMode?: boolean;
    emptyStateRevealReady?: boolean;
    hidePagination?: boolean;
    forms?: ReviewForm[];
    study?: Study;
  }>
> = ({
  displayProcedureModeToggle,
  pendingSlidesMode,
  emptyStateRevealReady = true,
  hidePagination = false,
  forms,
  study,
}) => {
  const [showGallery, setShowGallery] = useQueryParam('showGallery', BooleanParam);
  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);
  const [, setIsUploadSlidesModalOpen] = useQueryParam('isUploadSlidesModalOpen', BooleanParam);
  const [, setActiveDashboardTab] = useStudyDashboardTabState();
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const { hasPermission, hasSomeEditingPermissions } = useCasesAndSlidesEditingPermissions();

  const { casesViewMode } = useCasesViewMode();
  const canUseExperimentalFeatures = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const procedureViewMode = displayProcedureModeToggle ? casesViewMode : 'card';
  const canUseGalleryMode = displayProcedureModeToggle && canUseExperimentalFeatures;

  const theme = useTheme();
  const confirmedEmptyStudyIdRef = useRef<string | null>(null);

  const { isLoading: isCasesLoading, totalCasesCount, totalSlidesCount, procedures, error } = useCurrentStateCases();
  const { fieldsContext: proceduresFieldsContext } = useTableEditingContext<Procedure, ProceduresFieldsContext>();
  const { allMockCasesOfPendingSlides } = useAllPendingSlides({ enabled: Boolean(pendingSlidesMode && showGallery) });
  const slideCasePairs = flatMap(allMockCasesOfPendingSlides, (procedure: Procedure) =>
    map(procedure.slides, (slide) => ({
      slide,
      case: procedure,
    }))
  );

  const isLoading =
    Boolean(isCasesLoading) ||
    Boolean(proceduresFieldsContext.allCancerTypesLoading) ||
    Boolean(proceduresFieldsContext.stainTypesInStudyLoading);
  // Stain and cancer-type lookups do not decide whether the study has slides. Gating the empty
  // state on them makes Add slides disappear while those lookups refresh.
  const isCasesInventoryLoading = Boolean(isCasesLoading);

  const isStudyWithoutCases =
    Boolean(study?.id) &&
    (study?.casesCount === 0 || study?.casesCount === null) &&
    !queryParams.cohortId &&
    !pendingSlidesMode &&
    !isCasesInventoryLoading &&
    !error &&
    totalCasesCount === 0 &&
    procedures?.length === 0;
  const isStudyWithoutIngestedSlides =
    isStudyWithoutCases && (study?.slidesCount === 0 || study?.slidesCount === null) && totalSlidesCount === 0;

  // Case results omit unassigned slides, so inspect study-wide intake before choosing an empty state.
  const pendingInventory = useStudyPendingSlidesCount({
    studyId: study?.id,
    enabled: isStudyWithoutCases,
  });
  const uploadsQuery = useQuery(
    ['studyUploads', study?.id, labId],
    () => getUploadList({ studyId: study!.id, labId }),
    { enabled: isStudyWithoutCases, refetchInterval: 120000 }
  );
  const isCheckingStudyIntake =
    isStudyWithoutCases &&
    (uploadsQuery.isLoading || (pendingInventory.canQueryPendingSlides && pendingInventory.isLoading));
  const uploads = uploadsQuery.data;
  const uploadGroupsAreEmpty = [
    uploads?.all,
    uploads?.slides?.pending,
    uploads?.slides?.processed,
    uploads?.slides?.failed,
  ].every((group) => isEmpty(group) || (Array.isArray(group) && group.every((upload) => upload.isHidden === true)));
  const intakeConfirmsAbsence =
    pendingInventory.canQueryPendingSlides &&
    pendingInventory.isSuccess &&
    pendingInventory.totalPendingSlides === 0 &&
    uploadsQuery.isSuccess &&
    uploadGroupsAreEmpty;
  const intakeShowsContent =
    (pendingInventory.isSuccess && (pendingInventory.totalPendingSlides ?? 0) > 0) ||
    (uploadsQuery.isSuccess && !uploadGroupsAreEmpty);
  const actionableUploadGroups = getActionableUploadGroups(uploads);
  const unassignedSlideCount =
    actionableUploadGroups.find((group) => group.state === 'needsCaseAssignment')?.count || 0;
  const waitingUploadGroups = actionableUploadGroups.filter(
    (group) => group.state === 'ingesting' || group.state === 'queuedForIngest'
  );
  const waitingUploadSummary = waitingUploadGroups
    .map(
      ({ count, state }) =>
        `${count} ${count === 1 ? 'slide' : 'slides'} ${state === 'ingesting' ? 'ingesting' : 'queued for ingest'}`
    )
    .join(' · ');
  const canReviewUnassignedSlides =
    Boolean(study?.id) && pendingInventory.canQueryPendingSlides && hasPermission(Permission.ApplyCaseManifest);
  const caseManifestReviewUrl =
    study?.id && canReviewUnassignedSlides
      ? getOrganizationEntryUrl({
          studyId: study.id,
          labId,
          manifestState: {
            ...getDefaultManifestDialogUrlState(),
            reviewUnassignedSlidesWhenAvailable: true,
          },
          returnTo: { origin: 'dataset', labId, studyId: study.id, datasetMode: 'cases' },
        })
      : undefined;
  const canGoToWorkflow = hasPermission(Permission.ViewWorkflowMonitor);
  // A later refresh must not unmount a confirmed empty study. Background fetches set isFetching,
  // and a failed refresh keeps the last successful empty inventory.
  if (study?.id && isStudyWithoutIngestedSlides && intakeConfirmsAbsence) {
    confirmedEmptyStudyIdRef.current = study.id;
  }
  if (
    !study?.id ||
    study.id !== confirmedEmptyStudyIdRef.current ||
    intakeShowsContent ||
    Boolean(error) ||
    Boolean(queryParams.cohortId) ||
    Boolean(pendingSlidesMode) ||
    (study.slidesCount ?? 0) > 0 ||
    totalSlidesCount > 0 ||
    (procedures?.length ?? 0) > 0
  ) {
    confirmedEmptyStudyIdRef.current = null;
  }
  const showEmptyStudy = confirmedEmptyStudyIdRef.current === study?.id && isStudyWithoutIngestedSlides;
  const showCaseAssignmentEmptyState = isStudyWithoutCases && uploadsQuery.isSuccess && unassignedSlideCount > 0;
  const showIngestWaitingEmptyState =
    isStudyWithoutCases && uploadsQuery.isSuccess && unassignedSlideCount === 0 && waitingUploadGroups.length > 0;
  const isAddSlidesDisabled = !study?.name;

  return (
    <>
      {error ? (
        <ErrorMessage />
      ) : isCheckingStudyIntake ? (
        <Grid
          container
          className="dashboard-page-body"
          justifyContent="center"
          alignItems="center"
          sx={{ flex: '1 1 auto', minHeight: 240 }}
        >
          <Grid item>
            <Loader stage={LoadingStage.CheckingStudySlides} />
          </Grid>
        </Grid>
      ) : emptyStateRevealReady && showEmptyStudy ? (
        <StudySlidesEmptyState
          actionHidden={isPfizerDemoLab(labId)}
          actionDisabled={isAddSlidesDisabled}
          onAddSlides={() => setIsUploadSlidesModalOpen(true)}
          onGoToWorkflow={canGoToWorkflow ? () => setActiveDashboardTab(WorkflowTab) : undefined}
        />
      ) : emptyStateRevealReady && showCaseAssignmentEmptyState ? (
        <StudySlidesEmptyState
          title="Slides awaiting case assignment"
          description={`${unassignedSlideCount} ingested ${
            unassignedSlideCount === 1 ? 'slide is' : 'slides are'
          } ready for a case.`}
          supportingText={
            waitingUploadSummary
              ? `Suggested cases use slide file names and can be reviewed before applying. Also processing: ${waitingUploadSummary}.`
              : 'Suggested cases use slide file names and can be reviewed before applying.'
          }
          primaryAction={
            caseManifestReviewUrl
              ? {
                  label: 'Review case suggestions',
                  icon: <PlaylistAddCheckOutlinedIcon />,
                  href: caseManifestReviewUrl,
                }
              : null
          }
          onGoToWorkflow={canGoToWorkflow ? () => setActiveDashboardTab(WorkflowTab) : undefined}
        />
      ) : emptyStateRevealReady && showIngestWaitingEmptyState ? (
        <StudySlidesEmptyState
          title="Waiting for slide ingest"
          description="Uploaded slides become available for case assignment after ingest finishes."
          supportingText={waitingUploadSummary}
          primaryAction={
            hasPermission(Permission.ViewJobs)
              ? {
                  label: 'View ingest jobs',
                  icon: <WorkHistoryOutlinedIcon />,
                  href: getUrlToJobsPage({
                    labId,
                    jobsFilters: { studyId: study?.id, types: [JobType.Ingest, JobType.BatchIngest] },
                  }),
                }
              : null
          }
          onGoToWorkflow={canGoToWorkflow ? () => setActiveDashboardTab(WorkflowTab) : undefined}
        />
      ) : procedureViewMode === 'table' ? (
        <DataGridInterfaceWrapper
          casesInPage={procedures ?? []}
          totalSlides={totalSlidesCount}
          totalCases={totalCasesCount}
          forms={forms}
          isLoading={isLoading}
          disableEditing={!hasSomeEditingPermissions}
          pendingSlidesMode={pendingSlidesMode}
          hidePagination={hidePagination || (totalCasesCount === 0 && totalSlidesCount === 0)}
        />
      ) : isLoading ? (
        <Grid
          container
          className="dashboard-page-body"
          justifyContent="center"
          sx={{
            flex: '1 1 auto',
            ...getDashboardCanvasStyles(theme),
          }}
        >
          <Grid item>
            <Loader
              stage={slidesMode || pendingSlidesMode ? LoadingStage.LoadingSlidesList : LoadingStage.LoadingCaseList}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          {canUseGalleryMode &&
            showGallery &&
            (pendingSlidesMode ? (
              <UnpaginatedGalleryViewer slideCasePairs={slideCasePairs} onClose={() => setShowGallery(false)} />
            ) : (
              <GalleryViewer totalCases={totalCasesCount} totalSlides={totalSlidesCount} />
            ))}
          <ProcedureCards
            procedures={procedures ?? []}
            isLoading={isLoading}
            galleryView={procedureViewMode === 'gallery' && canUseGalleryMode}
            pendingSlidesMode={pendingSlidesMode}
          />
        </>
      )}
    </>
  );
};
